import React from "react";
import {
  Box,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { MealPlanHeaderCard } from "./StyledComponents";

/**
 * MealPlanHeader component displays the meal plan title and nutritional information
 *
 * @param {Object} mealPlan - The meal plan object to display
 * @param {Object} overallAverages - Average daily nutrition values
 * @param {Function} onEditClick - Function to handle edit button click
 */
const MealPlanHeader = ({ mealPlan, overallAverages, onEditClick }) => {
  if (!mealPlan) {
    return null;
  }

  // Use overallAverages if available, otherwise fall back to mealPlan totals (legacy)
  const nutritionData = overallAverages || mealPlan.totals || {};
  const avgFiber =
    nutritionData?.fiber ??
    nutritionData?.totalFiber ??
    0;

  return (
    <MealPlanHeaderCard>
      <CardContent sx={{ p: 3 }}>
        {/* Title and Edit Button */}
        <Grid container spacing={3} alignItems="center">
          <Grid item xs>
            <Stack spacing={1}>
              <Typography variant="h4" fontWeight="bold">
                {mealPlan.name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <RestaurantMenuIcon color="primary" fontSize="small" />
                <Typography variant="body1" color="text.secondary">
                  {mealPlan.selectedRecipes?.length || 0} recipes included
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          {onEditClick && (
            <Grid item>
              <Tooltip title="Edit Meal Plan">
                <IconButton
                  onClick={onEditClick}
                  sx={{
                    color: "primary.main",
                    bgcolor: "primary.light + '0A'",
                    "&:hover": {
                      bgcolor: "primary.light",
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Nutrition Stats Grid */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
            Average Daily Nutrition (choosing 1 option from each meal type)
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3} md>
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: 2,
                bgcolor: "grey.50",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Avg Calories
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {Math.round(nutritionData?.kCals || 0)} kCal
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3} md>
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: 2,
                bgcolor: "grey.50",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Avg Protein
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {Math.round(nutritionData?.proteins || 0)}g
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3} md>
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: 2,
                bgcolor: "grey.50",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Avg Carbs
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {Math.round(nutritionData?.carbs || 0)}g
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3} md>
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: 2,
                bgcolor: "grey.50",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Avg Fats
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {Math.round(nutritionData?.fats || 0)}g
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3} md>
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: 2,
                bgcolor: "grey.50",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Avg Fiber
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {Math.round(avgFiber)}g
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </MealPlanHeaderCard>
  );
};

export default MealPlanHeader;
